import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";

import { Paginator, PaginatorPageState } from "primereact/paginator";
import { ReportOptions } from "models/component-print-models";
import { ColumnAggregationType } from "utils/enums";
import { T } from "services/lang/LanguageService";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import DataCell from "./components/templates/table-components/data-cell/DataCell";
import { AttributeHeader } from "./components/templates/table-components/header/AttributeHeader";
import { Header } from "./components/templates/table-components/header/header";
import {
  IColumnsWithValuesProps,
  IProductTemplate,
  IProductTemplateAttribute,
  ITableDataEntry,
  ITableDataEntryValue,
} from "./utils/interfaces";
import {
  checkForHighlightType,
  checkForUnwantedType,
  formatDataTotalsAggregation,
  getHighlightColor,
  getValueColors,
} from "./utils/utils";

type ProductTemplateTableTransposedProps = {
  configData: IProductTemplate | undefined;
  templateData: Array<ITableDataEntry>;

  selectedEntryIndex: number | undefined;
  selectedEntryId: string | undefined;
  selectedEntryBody: ITableDataEntry | undefined;
  page: number;
  setPage: (page: number) => void;

  reportOptions?: ReportOptions;
  itemsPerPage?: number;
  foreignKeyWrap?: boolean;
  showTotals?: boolean;
  hidePagination?: boolean;

  handleTableKeyDown(key: string): void;
  EditRowDataCell(val: ITableDataEntry, index: number): JSX.Element;
  editedDataHandler(
    indexEntry: number,
    refName: string | undefined,
    newValue: any,
    newCell?: ITableDataEntryValue
  ): void;
  onClickSign(entryIndex: number, referenceName: string): void;
};

const ProductTemplateTableTransposed = ({
  templateData,
  configData,
  handleTableKeyDown,
  selectedEntryBody,
  selectedEntryId,
  selectedEntryIndex,
  EditRowDataCell,
  editedDataHandler,
  onClickSign,
  reportOptions,
  itemsPerPage,
  page,
  setPage,
  foreignKeyWrap,
  showTotals = false,
  hidePagination
}: ProductTemplateTableTransposedProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const tableId = useSafeId();

  const [columns, setColumns] = useState(itemsPerPage ?? 0);
  const [aggregatedColumns, setAggregatedColumns] = useState<IColumnsWithValuesProps[]>([]);

  const columnsPerPageOptions = itemsPerPage
    ? [...new Set([itemsPerPage, 10, 20, 30].sort((a, b) => a - b))]
    : [];

  const handleWheel = useCallback((event: WheelEvent) => {
    if (event.altKey) {
      event.preventDefault();
      const div: HTMLDivElement = event.currentTarget as HTMLDivElement;
      div.scrollLeft += event.deltaY * 0.45;
    }
  }, []);

  useEffect(() => {
    if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
      const table = document.querySelector(`#${tableId}`);
      if (table) {
        const allChildren = table.querySelectorAll("*");

        if (reportOptions.fontSize) {
          for (let i = 0; i < allChildren.length; i++) {
            allChildren[i].style.fontSize = reportOptions?.fontSize;
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.addEventListener("wheel", handleWheel);
    }

    return () => {
      if (divRef.current) {
        divRef.current.removeEventListener("wheel", handleWheel);
      }
    };
  }, [divRef, handleWheel]);

  useEffect(() => {
    setAggregatedColumns(formatDataTotalsAggregation(configData, templateData));
  }, [configData?.data?.attributes, templateData]);

  const handlePageChange = (e: PaginatorPageState) => {
    const newFirst = e.first;
    const newColumns = e.rows;
    setPage(newFirst);
    setColumns(newColumns);
  };

  const Row = (configVal: IProductTemplateAttribute, configIndex: number) => {
    if (templateData) {
      const slicedData = itemsPerPage ? templateData.slice(page, page + columns) : templateData;
      return slicedData.map((val, i) => rowDataCell(val, i, configVal, configIndex));
    }
    return null;
  };

  function rowDataCell(
    val: ITableDataEntry,
    index: number,
    configVal: IProductTemplateAttribute,
    configIndex: number
  ) {
    const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
    const obj = dataValue.entries[configVal.referenceName];

    let color;
    let bgColor;
    if (obj && obj.type && checkForHighlightType(obj.type)) {
      [color, bgColor] = getValueColors(obj.value as number, {
        highlightRanges: configVal.bgHighlightRanges,
        defaultHighlightColor: configVal.highlightColor,
      });
    } else {
      [color, bgColor] = getHighlightColor(configVal.highlightColor);
    }
    return (
      <td
        key={val.id}
        style={styleHandler(color, bgColor, obj && obj.type)}
        className="right-border data-cell even-cell odd-cell custom-td my-td"
      >
        <DataCell
          key={val.id}
          headerConfig={configVal}
          dataValue={dataValue}
          indexHeader={configIndex}
          indexEntry={index}
          changeHandler={editedDataHandler}
          isInEditMode={selectedEntryId === val.id}
          onClickSign={onClickSign}
          foreignKeyWrap={foreignKeyWrap}
          editRequireRoles={configVal.editRequireRoles ?? []}
        />
      </td>
    );
  }

  const EditRowComponents = () => {
    if (templateData) {
      const slicedData = itemsPerPage ? templateData.slice(page, page + columns) : templateData;
      return slicedData.map((val, i) => (
        <td
          key={val.id}
          style={{ textAlign: "center" }}
          className="right-border data-cell even-cell odd-cell custom-td my-td"
        >
          {EditRowDataCell(val, i)}
        </td>
      ));
    }
    return null;
  };

  const KeyColumn = (configVal: IProductTemplateAttribute, configIndex: number) => {
    function keyCell(val: ITableDataEntry, index: number, confVal: IProductTemplateAttribute, confIndex: number) {
      const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
      const obj = dataValue.entries[confVal.referenceName];

      let color;
      let bgColor;
      if (obj && obj.type && checkForHighlightType(obj.type)) {
        [color, bgColor] = getValueColors(obj.value as number, {
          highlightRanges: confVal.bgHighlightRanges,
          defaultHighlightColor: confVal.highlightColor,
        });
      } else {
        [color, bgColor] = getHighlightColor(confVal.highlightColor);
      }

      return (
        <th
          key={val.id}
          style={styleHandler(color, bgColor, obj && obj.type)}
          className="right-border data-cell even-cell odd-cell custom-th my-th"
        >
          <DataCell
            key={val.id}
            headerConfig={confVal}
            dataValue={dataValue}
            indexHeader={confIndex}
            indexEntry={index}
            changeHandler={editedDataHandler}
            isInEditMode={selectedEntryIndex === index}
            onClickSign={onClickSign}
            editRequireRoles={confVal.editRequireRoles ?? []}
          />
        </th>
      );
    }

    if (templateData) {
      const slicedData = itemsPerPage ? templateData.slice(page, page + columns) : templateData;
      return slicedData.map((val, i) => keyCell(val, i, configVal, configIndex));
    }
    return null;
  };

  const styleHandler = (
    color: string | undefined,
    bgColor: string | undefined,
    type: string | undefined
  ): CSSProperties | undefined => {
    if (checkForUnwantedType(type)) {
      if (color && bgColor) {
        return {
          textAlign: "center",
          minWidth: "200px",
          maxWidth: "200px",
          color,
          backgroundColor: bgColor,
        };
      }

      if (color) {
        return {
          textAlign: "center",
          minWidth: "200px",
          maxWidth: "200px",
          color,
        };
      }

      if (bgColor) {
        return {
          textAlign: "center",
          minWidth: "200px",
          maxWidth: "200px",
          backgroundColor: bgColor,
        };
      }
    }

    return {
      textAlign: "center",
      minWidth: "200px",
      maxWidth: "200px",
    };
  };

  return (
    <div
      ref={divRef}
      onKeyDown={e => handleTableKeyDown(e.key)}
      className="transposed-table-wrapper"
    >
      <table id={tableId} className="transposed-table">
        <thead className="transposed-thead">
          <tr>
            {configData &&
              configData.data?.attributes.map((val: IProductTemplateAttribute, i: number) => {
                if (val.referenceName === configData.keyColumn) {
                  return (
                    <React.Fragment key={val.referenceName}>
                      <AttributeHeader key={val.referenceName} attribute={val} />
                      {KeyColumn(val, i)}
                      {showTotals && <TotalColumn aggregatedColumns={aggregatedColumns} i={i} />}
                    </React.Fragment>
                  );
                }

                return null;
              })}
          </tr>
        </thead>
        <tbody className="transposed-tbody">
          {configData &&
            configData.data?.attributes.map((val: IProductTemplateAttribute, i: number) => {
              if (val.referenceName !== configData.keyColumn) {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr style={{ margin: "5px" }} key={i}>
                    <AttributeHeader attribute={val} />
                    {Row(val, i)}
                    {showTotals && <TotalColumn aggregatedColumns={aggregatedColumns} i={i} />}
                  </tr>
                );
              }
              return null;
            })}
          {configData && (
            <tr className="non-printable">
              <Header />
              {EditRowComponents()}
            </tr>
          )}
        </tbody>
      </table>
      {itemsPerPage && !hidePagination && (
        <Paginator
          totalRecords={templateData.length}
          rows={columns}
          first={page}
          onPageChange={handlePageChange}
          template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={`${T("word.showing")} {first} ${T("word.to")} {last} ${T("word.of")} {totalRecords}`}
          rowsPerPageOptions={columnsPerPageOptions}
        />
      )}
    </div>
  );
};

const TotalColumn = ({
  aggregatedColumns,
  i,
}: {
  aggregatedColumns: IColumnsWithValuesProps[];
  i: number;
}) => {
  return (
    <td className="total">
      {aggregatedColumns[i]?.aggregationType === ColumnAggregationType.COUNT && (
        <span title={aggregatedColumns[i]?.aggregationType} className="px-3">
          {aggregatedColumns[i]?.total}
        </span>
      )}
      {(aggregatedColumns[i]?.aggregationType === ColumnAggregationType.MEAN ||
        aggregatedColumns[i]?.aggregationType === ColumnAggregationType.SUM) && (
        <span
          title={aggregatedColumns[i]?.aggregationType}
        >{`${aggregatedColumns[i]?.total ?? ""} ${aggregatedColumns[i]?.unit ?? ""}`}</span>
      )}
    </td>
  );
};
export default ProductTemplateTableTransposed;
